import { css, SerializedStyles } from '@emotion/react';
import { omit, pick } from 'ramda';
import * as React from 'react';

import colors from '../../theme/colors';
import { spacingKeys, spacingsCss } from '../../theme/variables';
import Loader from '../common/Loader';
import { ButtonProps } from './Button.types';

export enum ButtonSize {
  ExtraSmall = 'extra-small',
  Small = 'small',
  Medium = 'medium',
  Large = 'large'
}

export enum ButtonState {
  Base = 'base',
  Hover = 'hover',
  Disabled = 'disabled',
  Loading = 'loading'
}

export enum ButtonVariant {
  Primary = 'primary',
  Outline = 'outline',
  Destructive = 'destructive',
  DestructiveOutline = 'destructive-outline',
  Ghost = 'ghost',
  Link = 'Link',
  Secondary = 'secondary'
}

const SIZE_MAP = {
  [ButtonSize.ExtraSmall]: css`
    padding: 2px 4px;
  `,
  [ButtonSize.Small]: css`
    padding: 4px 8px;
  `,
  [ButtonSize.Medium]: css`
    padding: 8px 16px;
  `,
  [ButtonSize.Large]: css`
    padding: 12px 20px;
  `
};

const VARIANT_MAP = {
  [ButtonVariant.Primary]: {
    base: [colors.blue400, colors.blue400, colors.white],
    hover: [colors.blue300, colors.blue300, colors.white],
    disabled: [colors.neutral100, colors.neutral100, colors.neutral400],
    loading: [colors.neutral100, colors.neutral100, colors.neutral400]
  },
  [ButtonVariant.Outline]: {
    base: [colors.white, colors.blue400, colors.blue400],
    hover: [colors.neutral100, colors.blue500, colors.blue500],
    disabled: [colors.white, colors.neutral100, colors.neutral400],
    loading: [colors.white, colors.neutral100, colors.neutral400]
  },
  [ButtonVariant.Destructive]: {
    base: [colors.red400, colors.red400, colors.white],
    hover: [colors.red300, colors.red300, colors.white],
    disabled: [colors.neutral100, colors.neutral100, colors.neutral400],
    loading: [colors.neutral100, colors.neutral100, colors.neutral400]
  },
  [ButtonVariant.DestructiveOutline]: {
    base: [colors.white, colors.red400, colors.red400],
    hover: [colors.white, colors.red300, colors.red300],
    disabled: [colors.white, colors.white, colors.red200],
    loading: [colors.white, colors.white, colors.red200]
  },
  [ButtonVariant.Ghost]: {
    base: ['transparent', 'transparent', colors.blue600],
    hover: [colors.neutral100, colors.neutral100, colors.blue500],
    disabled: ['transparent', 'transparent', colors.neutral400],
    loading: ['transparent', 'transparent', colors.blue500]
  },
  [ButtonVariant.Link]: {
    base: ['transparent', 'transparent', colors.blue400],
    hover: ['transparent', 'transparent', colors.blue300],
    disabled: ['transparent', 'transparent', colors.neutral400],
    loading: ['transparent', 'transparent', colors.neutral400]
  },
  [ButtonVariant.Secondary]: {
    base: [colors.blue50, colors.blue50, colors.blue500],
    hover: [colors.blue100, colors.blue100, colors.blue600],
    disabled: [colors.white, colors.white, colors.neutral400],
    loading: [colors.blue50, colors.blue50, colors.blue500]
  }
};

const buttonColors = (bg: string, border: string, color: string): SerializedStyles => css`
  background-color: ${bg};
  border: 1px solid ${border};
  color: ${color};
`;

const getColors = (variant: ButtonVariant, state: ButtonState) => {
  const [bg = colors.white, border = colors.white, color = colors.blue600] = VARIANT_MAP[variant][state];
  return buttonColors(bg, border, color);
};

const borderlessCss = (loading: boolean): SerializedStyles => css`
  border: none;
  background: transparent;
  padding: 1px 6px;
  color: ${colors.blue400};

  &:hover {
    border: none;
    background: transparent;
    ${
      !loading &&
      css`
        color: ${colors.blue300};
      `
    }
  }

  &:disabled,
  &:disabled:hover {
    background: transparent;
    color: ${colors.neutral300};
    cursor: not-allowed;
  }
`;

const buttonCss = (
  size: ButtonSize,
  variant: ButtonVariant,
  loading: boolean,
  fullWidth: boolean,
  noPadding: boolean,
  iconOnly: boolean,
  borderless: boolean
): SerializedStyles => css`
  ${SIZE_MAP[size]};
  ${getColors(variant, loading ? ButtonState.Loading : ButtonState.Base)};
  display: inline-flex;
  justify-content: center;
  align-items: center;
  position: relative;
  gap: 4px;

  font-size: 14px;
  line-height: 16px;
  font-weight: 600;

  width: ${fullWidth ? '100%' : 'auto'};
  border-radius: 8px;
  box-sizing: border-box;
  cursor: pointer;
  -webkit-font-smoothing: antialiased;

  transition:
    color 0.3s,
    background-color 0.3s,
    border-color 0.3s;

  ${
    variant === ButtonVariant.Link &&
    css`
      padding: 0;
    `
  }

  &:hover {
    ${!loading && getColors(variant, ButtonState.Hover)};
    ${
      variant === ButtonVariant.Link &&
      css`
        text-decoration: underline;
        text-decoration-thickness: 1px;
        text-underline-offset: 2px;
      `
    }
  }

  &:disabled,
  &:disabled:hover {
    cursor: not-allowed;
    ${getColors(variant, ButtonState.Disabled)}
  }
  ${
    variant === ButtonVariant.Link &&
    css`
      padding: 0;
    `
  };

  ${noPadding ? 'padding: 0;' : ''}
  ${iconOnly ? 'padding: 8px;' : ''}
  ${borderless ? borderlessCss(loading) : ''}
`;

const Button = ({
  size = ButtonSize.Medium,
  children,
  variant = ButtonVariant.Primary,
  customCss,
  disabled,
  type = 'button',
  fullWidth = false,
  loading = false,
  innerRef,
  noPadding = false,
  iconOnly = false,
  borderless = false,
  ...props
}: ButtonProps): React.JSX.Element => {
  const spacingProps = pick(spacingKeys, props);
  const restOfProps = omit(spacingKeys, props);

  return (
    <button
      {...restOfProps}
      ref={innerRef}
      type={type}
      disabled={disabled || loading}
      css={[
        buttonCss(size, variant, loading, fullWidth, noPadding, iconOnly, borderless),
        spacingsCss(spacingProps),
        customCss
      ]}
    >
      {loading && <Loader width={16} height={16} />}
      {children}
    </button>
  );
};

export default Button;
